import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import AppInput from '../../components/AppInput/AppInput';
import { useSelector, useDispatch } from 'react-redux';
import TextArea from '../../components/AppInput/TextArea';
import { setApiErrorJson, setApiJson } from '../../redux/Actions/ApiAction';
import AppButton from '../../components/AppButton/AppButton';
import { HitApi } from '../../Api/ApiHit';
import { Tag, Percent, DollarSign, Calendar, Package, Users } from 'lucide-react';
import { CouponValidation } from '../../validationscheema/CouponValidation';
import { addCoupon, searchCoupon, updateCoupon } from '../../constant/Constant';
import { getElevateUser, getSelectedUnit } from '../../storage/Storage';
import SearchableSelect from '../../components/AppSelect/SearchableSelect';
import toast from 'react-hot-toast';
import { getErrorMsg, hasError } from '../../utils/utils';
import AppSwitch from '../../components/Switch/AppSwitch';
import { applicableOnOptions, discountTypeOptions } from '../../constant/Options';
import DateTimePicker from '../../components/DateAndTime/DateTimePicker';

function AddCoupon() {
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: urlId } = useParams();

    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const initialCouponData = {
            code: "",
            name: "",
            description: "",
            discountType: "percentage",
            discountValue: "",
            maxDiscountAmount: "",
            applicableOn: "both",
            specificServices: [],
            specificProducts: [],
            minPurchaseAmount: "",
            maxUsageLimit: "",
            usedCount: 0,
            perUserLimit: "",
            startTimestamp: "",
            endTimestamp: "",
            isActive: true,
            isAppCoupon: false,
            createdBy: getElevateUser()?._id,
            unitIds: getSelectedUnit()?._id
        };

        if (urlId) {
            setIsEditMode(true);
            loadCouponData();
        } else {
            dispatch(setApiJson(initialCouponData));
            dispatch(setApiErrorJson({}));
        }
    }, [dispatch, urlId]);

    const loadCouponData = () => {
        setIsLoadingData(true);
        const json = {
            page: 1,
            limit: 10,
            search: {
                _id: urlId
            }
        };

        HitApi(json, searchCoupon).then((res) => {
            if (res?.statusCode === 200 || res?.docs) {
                const couponData = res?.docs?.[0] || res?.data?.docs?.[0];

                if (couponData) {
                    // Convert timestamps to datetime-local format for input fields
                    const formattedData = {
                        ...couponData,
                        startTimestamp: couponData.startTimestamp ? 
                            new Date(couponData.startTimestamp).toISOString().slice(0, 16) : "",
                        endTimestamp: couponData.endTimestamp ? 
                            new Date(couponData.endTimestamp).toISOString().slice(0, 16) : "",
                        maxDiscountAmount: couponData.maxDiscountAmount || "",
                        minPurchaseAmount: couponData.minPurchaseAmount || "",
                        perUserLimit: couponData.perUserLimit || "",
                        // Ensure code is uppercase
                        code: couponData.code?.toUpperCase() || ""
                    };
                    dispatch(setApiJson(formattedData));
                } else {
                    toast.error('Coupon not found');
                    navigate('/coupon');
                }
            } else {
                toast.error('Failed to load coupon data');
                navigate('/coupon');
            }
        }).catch((error) => {
            console.error("Error loading coupon data:", error);
            toast.error('Error loading coupon data. Please try again.');
            navigate('/coupon');
        }).finally(() => {
            setIsLoadingData(false);
        });
    };

    const handleSubmit = () => {
        setIsSubmitting(true);

        // Clean the data - convert empty strings to appropriate values
        const cleanedData = {
            ...ApiReducer.apiJson,
            // Ensure code is uppercase and trimmed
            code: ApiReducer.apiJson.code?.toUpperCase().trim(),
            name: ApiReducer.apiJson.name?.trim(),
            description: ApiReducer.apiJson.description?.trim(),
            discountValue: Number(ApiReducer.apiJson.discountValue),
            maxDiscountAmount: ApiReducer.apiJson.maxDiscountAmount === "" || 
                               ApiReducer.apiJson.maxDiscountAmount === null || 
                               ApiReducer.apiJson.maxDiscountAmount === undefined 
                               ? undefined 
                               : Number(ApiReducer.apiJson.maxDiscountAmount),
            minPurchaseAmount: ApiReducer.apiJson.minPurchaseAmount === "" || 
                              ApiReducer.apiJson.minPurchaseAmount === null || 
                              ApiReducer.apiJson.minPurchaseAmount === undefined 
                              ? 0 
                              : Number(ApiReducer.apiJson.minPurchaseAmount),
            maxUsageLimit: ApiReducer.apiJson.maxUsageLimit === "" || 
                          ApiReducer.apiJson.maxUsageLimit === null || 
                          ApiReducer.apiJson.maxUsageLimit === undefined 
                          ? undefined 
                          : Number(ApiReducer.apiJson.maxUsageLimit),
            perUserLimit: ApiReducer.apiJson.perUserLimit === "" || 
                         ApiReducer.apiJson.perUserLimit === null || 
                         ApiReducer.apiJson.perUserLimit === undefined 
                         ? undefined 
                         : Number(ApiReducer.apiJson.perUserLimit),
            usedCount: Number(ApiReducer.apiJson.usedCount) || 0,
        };

        CouponValidation(cleanedData).then((errors) => {
            dispatch(setApiErrorJson(errors));
            if (Object.keys(errors).length === 0) {
                const couponData = {
                    ...cleanedData,
                    id: urlId,
                    unitIds: getSelectedUnit()?._id,
                    createdBy: getElevateUser()?._id,
                    startTimestamp: cleanedData.startTimestamp ? 
                        new Date(cleanedData.startTimestamp).getTime() : Date.now(),
                    endTimestamp: cleanedData.endTimestamp ? 
                        new Date(cleanedData.endTimestamp).getTime() : null,
                };

                // Remove undefined values
                Object.keys(couponData).forEach(key => {
                    if (couponData[key] === undefined) {
                        delete couponData[key];
                    }
                });

                const apiCall = isEditMode ? updateCoupon : addCoupon;
                const successMsg = isEditMode ? 'Coupon updated successfully!' : 'Coupon created successfully!';

                HitApi(couponData, apiCall).then((res) => {
                    if (res.statusCode === 200 || res.statusCode === 201) {
                        toast.success(successMsg);
                        navigate('/coupon');
                    } else {
                        toast.error(res?.message || "Failed to save coupon");
                    }
                }).catch((error) => {
                    console.error("Submit error:", error);
                    toast.error("Error saving coupon. Please try again.");
                }).finally(() => {
                    setIsSubmitting(false);
                });
            } else {
                setIsSubmitting(false);
                toast.error('Please fix the validation errors');
            }
        }).catch((validationError) => {
            console.error("Validation error:", validationError);
            toast.error('Validation failed. Please check your inputs.');
            setIsSubmitting(false);
        });
    };

    if (isLoadingData) {
        return (
            <div className="p-5">
                <PageHeader
                    title={isEditMode ? 'Edit Coupon' : 'Add Coupon'}
                    description={isEditMode ? 'Update Coupon Information' : 'Create a new discount coupon'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-gray-500">Loading coupon data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title={isEditMode ? 'Edit Coupon' : 'Add Coupon'}
                description={isEditMode ? 'Update Coupon Information' : 'Create a new discount coupon'}
            />

            <div className="bg-white p-8 border rounded-xl mb-6">
                {/* Basic Information Section */}
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Coupon Information</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <AppInput
                        title="Coupon Code"
                        placeholder="e.g., SAVE50, WINTER20"
                        name="code"
                        important={true}
                        error={hasError('code', ApiReducer)}
                        errormsg={getErrorMsg('code', ApiReducer)}
                        icon={<Tag size={18} />}
                        disabled={isSubmitting}
                        value={ApiReducer?.apiJson?.code?.toUpperCase() || ""}
                        specification="Code will be automatically converted to uppercase"
                    />

                    <AppInput
                        title="Coupon Name"
                        placeholder="e.g., 20% Off Winter Sale"
                        name="name"
                        important={true}
                        error={hasError('name', ApiReducer)}
                        errormsg={getErrorMsg('name', ApiReducer)}
                        icon={<Tag size={18} />}
                        disabled={isSubmitting}
                    />
                </div>

                <div className="mb-6">
                    <TextArea
                        title="Description"
                        placeholder="Describe the coupon offer and terms"
                        name="description"
                        rows={3}
                        disabled={isSubmitting}
                        error={hasError('description', ApiReducer)}
                        errormsg={getErrorMsg('description', ApiReducer)}
                    />
                </div>

                {/* Discount Configuration Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Discount Configuration</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <SearchableSelect
                            title="Discount Type"
                            name="discountType"
                            options={discountTypeOptions}
                            placeholder="Select Discount Type"
                            icon={<Percent size={18} />}
                            disabled={isSubmitting}
                            important={true}
                            error={hasError('discountType', ApiReducer)}
                            errormsg={getErrorMsg('discountType', ApiReducer)}
                        />

                        <AppInput
                            title={ApiReducer?.apiJson?.discountType === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (₹)'}
                            placeholder={ApiReducer?.apiJson?.discountType === 'percentage' ? 'Enter percentage (0-100)' : 'Enter fixed amount'}
                            name="discountValue"
                            important={true}
                            error={hasError('discountValue', ApiReducer)}
                            errormsg={getErrorMsg('discountValue', ApiReducer)}
                            icon={ApiReducer?.apiJson?.discountType === 'percentage' ? <Percent size={18} /> : <DollarSign size={18} />}
                            type="number"
                            step="0.01"
                            min="0"
                            max={ApiReducer?.apiJson?.discountType === 'percentage' ? "100" : undefined}
                            suffix={ApiReducer?.apiJson?.discountType === 'percentage' ? '%' : ''}
                            prefix={ApiReducer?.apiJson?.discountType === 'fixed' ? '₹' : ''}
                            disabled={isSubmitting}
                        />
                    </div>

                    {ApiReducer?.apiJson?.discountType === 'percentage' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <AppInput
                                title="Maximum Discount Amount (Optional)"
                                placeholder="Enter max discount cap"
                                name="maxDiscountAmount"
                                error={hasError('maxDiscountAmount', ApiReducer)}
                                errormsg={getErrorMsg('maxDiscountAmount', ApiReducer)}
                                icon={<DollarSign size={18} />}
                                type="number"
                                step="0.01"
                                min="0"
                                prefix="₹"
                                disabled={isSubmitting}
                                specification="Set a maximum cap for percentage discounts"
                            />
                        </div>
                    )}
                </div>

                {/* Applicability Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Applicability</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <SearchableSelect
                            title="Applicable On"
                            name="applicableOn"
                            options={applicableOnOptions}
                            placeholder="Select Applicability"
                            icon={<Package size={18} />}
                            disabled={isSubmitting}
                            important={true}
                            error={hasError('applicableOn', ApiReducer)}
                            errormsg={getErrorMsg('applicableOn', ApiReducer)}
                        />

                        <AppInput
                            title="Minimum Purchase Amount (Optional)"
                            placeholder="Enter minimum purchase amount"
                            name="minPurchaseAmount"
                            error={hasError('minPurchaseAmount', ApiReducer)}
                            errormsg={getErrorMsg('minPurchaseAmount', ApiReducer)}
                            icon={<DollarSign size={18} />}
                            type="number"
                            step="0.01"
                            min="0"
                            prefix="₹"
                            disabled={isSubmitting}
                            specification="Minimum order value required to apply this coupon (defaults to 0)"
                        />
                    </div>
                </div>

                {/* Usage Limits Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Usage Limits</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <AppInput
                            title="Maximum Usage Limit (Optional)"
                            placeholder="Enter max total uses"
                            name="maxUsageLimit"
                            error={hasError('maxUsageLimit', ApiReducer)}
                            errormsg={getErrorMsg('maxUsageLimit', ApiReducer)}
                            icon={<Tag size={18} />}
                            type="number"
                            min="1"
                            disabled={isSubmitting}
                            specification="Total number of times this coupon can be used across all users"
                        />

                        <AppInput
                            title="Per User Limit (Optional)"
                            placeholder="Enter max uses per user"
                            name="perUserLimit"
                            error={hasError('perUserLimit', ApiReducer)}
                            errormsg={getErrorMsg('perUserLimit', ApiReducer)}
                            icon={<Users size={18} />}
                            type="number"
                            min="1"
                            disabled={isSubmitting}
                            specification="Maximum number of times a single user can use this coupon"
                        />
                    </div>

                    {isEditMode && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <AppInput
                                title="Already Used Count"
                                placeholder="Used count"
                                name="usedCount"
                                icon={<Tag size={18} />}
                                type="number"
                                disabled={true}
                                value={ApiReducer?.apiJson?.usedCount || 0}
                                specification="Number of times coupon has been used (read-only)"
                            />
                        </div>
                    )}
                </div>

                {/* Validity Period Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Validity Period</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <DateTimePicker
                            title="Start Date & Time"
                            placeholder="Select start date"
                            name="startTimestamp"
                            important={true}
                            error={hasError('startTimestamp', ApiReducer)}
                            errormsg={getErrorMsg('startTimestamp', ApiReducer)}
                            icon={<Calendar size={18} />}
                            type="datetime-local"
                            disabled={isSubmitting}
                            specification="When the coupon becomes active (defaults to now if not set)"
                        />

                        <DateTimePicker
                            title="End Date & Time"
                            placeholder="Select end date"
                            name="endTimestamp"
                            important={true}
                            error={hasError('endTimestamp', ApiReducer)}
                            errormsg={getErrorMsg('endTimestamp', ApiReducer)}
                            icon={<Calendar size={18} />}
                            type="datetime-local"
                            disabled={isSubmitting}
                            specification="When the coupon expires"
                        />
                    </div>
                </div>

                {/* Status Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Status</h3>
                    <div className="flex items-center space-x-3">
                        <AppSwitch
                            name="isActive"
                            checked={ApiReducer?.apiJson?.isActive}
                            disabled={isSubmitting}
                        />
                        <label className="text-sm font-medium text-gray-700">
                            {ApiReducer?.apiJson?.isActive ? 'Coupon is Active' : 'Coupon is Inactive'}
                        </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Inactive coupons cannot be used by customers even if within validity period
                    </p>

                    <div className="flex items-center space-x-3 mt-4">
                        <AppSwitch
                            name="isAppCoupon"
                            checked={ApiReducer?.apiJson?.isAppCoupon}
                            disabled={isSubmitting}
                        />
                        <label className="text-sm font-medium text-gray-700">
                            {ApiReducer?.apiJson?.isAppCoupon ? 'Visible on App' : 'Hidden from App'}
                        </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Enable to show this coupon in the mobile app's Offers & Coupons section
                    </p>
                </div>

                {/* Submit Button */}
                <div className='flex mt-5 justify-end space-x-3'>
                    <AppButton
                        buttontext={isSubmitting ? "Processing..." : (isEditMode ? "Update Coupon" : "Create Coupon")}
                        onClick={handleSubmit}
                        className="px-8 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                    />
                </div>
            </div>
        </div>
    );
}

export default AddCoupon;